// „JELSZÓ BEÁLLÍTÁSA" (`/reset-password?token=…`) — PUBLIKUS oldal: a levélből kapott token MAGA a bizonyíték.
// A jelszó-visszaállító ÉS a köszöntő levél („első jelszó beállítása") ugyanide vezet.
//
// ══ A TOKEN A QUERY-STRINGBEN UTAZIK — EZÉRT HÁROM DOLGOT KELL TENNI ═══════════════════════════════════
// 1. **AZONNALI `history.replaceState`**: a token nélküli URL-re írjuk át a címsort, még a beküldés előtt. A
//    query-string ugyanis SZIVÁROG (böngésző-history, megosztott képernyő, a felhasználó másolja/beilleszti a
//    linket) — a token pedig fiók-átvételi kar, amíg le nem jár. A tokent ezután a komponens STATE-je tartja.
// 2. **`Referrer-Policy: no-referrer`** (globálisan, `index.html`) — enélkül a lapról induló bármely külső
//    kérés `Referer` fejlécében a tokenes URL utazna.
// 3. **A token SOHA nem kerül telemetriába/logba** — ezért nem is naplózzuk, és a hibaüzenetek sem tükrözik.
//
// A HIBA-KÉPZÉS a BFF szerződését tükrözi: 400 → GENERIKUS („érvénytelen vagy lejárt link") + „új link kérése"
// kijárat; 422 → a policy-hibák a JELSZÓ-MEZŐHÖZ kötve, a szerver GÉPI KÓDJAIBÓL fordítva (`too_short`,
// `missing_uppercase`, …). A szerver a policy AUTORITÁSA — a lap csak tükrözi.
import { ResetPasswordBodySchema } from '@atlas/contracts';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { PasswordPolicyNote } from '@/components/shared/password-policy-note';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { api } from '@/lib/api';

/** A `?token=` search-param. Hiányzó/üres token → a lap a „kérj új linket" állapotot mutatja. */
const resetSearchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search) => resetSearchSchema.parse(search),
  component: ResetPasswordRoute,
});

/**
 * A FORM-séma: a contract `newPassword` mezője + a KLIENS-OLDALI megerősítés.
 *
 * A tartalmi jelszó-policy (hossz, karakter-osztályok) SZÁNDÉKOSAN nincs itt: annak EGYETLEN autoritása a
 * szerver (Odin), és a 422-es, mezőhöz kötött kódokból írjuk ki. A megerősítés-egyezés viszont TISZTÁN
 * kliens-oldali fogalom (a szerver nem is látja a második mezőt) — ezért az itt él.
 */
const formSchema = ResetPasswordBodySchema.pick({ newPassword: true })
  .extend({ confirm: z.string() })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: 'MISMATCH', // a szöveget a komponens i18n-eli (a séma nyelv-független)
  });

type ResetValues = z.infer<typeof formSchema>;

/**
 * Route-wrapper: a search-paramot olvassa, és a ROUTER-nek is szól a token eltakarításáról.
 *
 * MIÉRT KÉT TAKARÍTÁS (`replaceState` a komponensben + `navigate` itt): a `window.history.replaceState`
 * AZONNAL hat a címsorra (a leggyorsabb, és router nélkül is működik — ezért az a komponensben van), de
 * KIKERÜLI a TanStack Router belső location/search-állapotát: ott a `token` megmaradna, és egy jövőbeli
 * router-vezérelt URL-írás (`navigate({ to: '.' })`, `router.invalidate()`, search-frissítés) VISSZAÍRHATNÁ a
 * címsorba. A router-szintű `navigate` ezt zárja. A `replace: true` azért kell, hogy a visszalépés se hozza
 * vissza a tokenes URL-t a history-ba.
 */
function ResetPasswordRoute(): JSX.Element {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [held] = useState(token ?? null);

  useEffect(() => {
    if (token !== undefined && token !== '') {
      void navigate({ to: '/reset-password', search: {}, replace: true });
    }
    // Csak MOUNT-kor (a takarítás után a `token` már üres — nem akarunk hurkot).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ResetPasswordPage token={held} />;
}

export function ResetPasswordPage({ token }: { token: string | null }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /**
   * A token a KOMPONENS állapotában él, nem az URL-ben: a `useEffect` az első renderrel kitörli a címsorból
   * (lásd a fájl-fejléc 1. pontját). A `useState` inicializátor a MOUNT-kori értéket fogja meg, tehát a
   * takarítás nem viszi el a tokent a beküldés alól.
   */
  const [heldToken] = useState(token);
  /** Szerver-oldali policy-kódok (`too_short`, …) — a jelszó-mező alatt jelennek meg. */
  const [policyCodes, setPolicyCodes] = useState<string[]>([]);
  /** GENERIKUS link-hiba (400): a lap „új link kérése" állapotra vált. */
  const [linkInvalid, setLinkInvalid] = useState(false);
  const reset = api.resetPassword.useMutation();

  // A TOKEN ELTÁVOLÍTÁSA a címsorból, azonnal az első renderen. `replaceState` (nem `pushState`): a visszalépés
  // se hozza vissza a tokenes URL-t a history-ba.
  useEffect(() => {
    if (token !== null && token !== '') {
      window.history.replaceState(null, '', window.location.pathname);
    }
    // Szándékosan csak MOUNT-kor fut (a `token` a search-paramból jön, ami a takarítás után üres lesz).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { newPassword: '', confirm: '' },
  });

  const onSubmit = handleSubmit((values) => {
    if (heldToken === null || heldToken === '') return;
    setPolicyCodes([]);
    reset.mutate(
      { body: { token: heldToken, newPassword: values.newPassword } },
      {
        onSuccess: () => {
          // A beváltás a felhasználó ÖSSZES session-jét érvényteleníti (Odin-oldalon) — az új jelszóval a
          // login-oldalon lép be. Ezért NEM próbálunk automatikusan bejelentkeztetni.
          toast.success(t('resetPassword.success'));
          void navigate({ to: '/login' });
        },
        onError: (err) => {
          const status = (err as { status?: number } | null)?.status;
          if (status === 422) {
            // A szerver GÉPI KÓDJAI a `newPassword` mezőre — a UI a saját nyelvén írja ki őket.
            const errorBody = (err as { body?: { errors?: Record<string, string[]> } } | null)
              ?.body;
            setPolicyCodes(errorBody?.errors?.newPassword ?? ['invalid']);
            return;
          }
          if (status === 400) {
            // GENERIKUS: „nincs ilyen" / „lejárt" / „már felhasználták" — az OKOT nem tudjuk és nem is találgatjuk.
            setLinkInvalid(true);
            return;
          }
          toast.error(
            t(status === 429 ? 'resetPassword.errRateLimited' : 'resetPassword.errUnavailable'),
          );
        },
      },
    );
  });

  const backToLogin = (
    <Link
      to="/login"
      className="text-foreground inline-flex items-center gap-1.5 font-medium hover:underline"
    >
      <ArrowLeftIcon className="h-3.5 w-3.5" />
      {t('resetPassword.backToLogin')}
    </Link>
  );

  // ── HIÁNYZÓ vagy ELUTASÍTOTT token → tiszta üzenet + „új link kérése" kijárat ────────────────────────
  if (heldToken === null || heldToken === '' || linkInvalid) {
    return (
      <AuthShell
        title={t('resetPassword.invalidTitle')}
        subtitle={t('resetPassword.invalidSubtitle')}
        footer={backToLogin}
      >
        <div className="bg-muted/40 flex gap-3 rounded-lg border p-4" role="alert">
          <ExclamationTriangleIcon className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-sm">{t('resetPassword.invalidBody')}</p>
        </div>
        <Button variant="brand" asChild size="lg" className="mt-4 w-full">
          <Link to="/forgot-password">{t('resetPassword.requestNew')}</Link>
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('resetPassword.title')}
      subtitle={t('resetPassword.subtitle')}
      footer={backToLogin}
    >
      <form onSubmit={onSubmit}>
        <div className="mb-3.5 space-y-1.5">
          <Label htmlFor="newPassword" className="text-sm font-medium">
            {t('resetPassword.newPassword')}
          </Label>
          <PasswordInput
            id="newPassword"
            autoComplete="new-password"
            className="h-[38px]"
            autoFocus
            {...register('newPassword')}
          />
          {/* A statikus szabály-hint + a szerver policy-kódjai a MEZŐHÖZ kötve — KÖZÖS komponens (ugyanez
              a blokk az „Új munkatárs" kezdő-jelszó mezőjén; ismeretlen kód → generikus szöveg). */}
          <PasswordPolicyNote codes={policyCodes} />
        </div>

        <div className="mb-5 space-y-1.5">
          <Label htmlFor="confirm" className="text-sm font-medium">
            {t('resetPassword.confirm')}
          </Label>
          <PasswordInput
            id="confirm"
            autoComplete="new-password"
            className="h-[38px]"
            {...register('confirm')}
          />
          {errors.confirm && (
            <p className="text-destructive text-xs">{t('resetPassword.mismatch')}</p>
          )}
        </div>

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={reset.isPending}
        >
          {reset.isPending ? (
            t('resetPassword.submitting')
          ) : (
            <>
              {t('resetPassword.submit')} <CheckCircleIcon />
            </>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
